/* eslint-disable max-len */
export const transitionType = [
  {
    class: 'fadeIn',
    name: 'Fade in',
  },
  {
    class: 'spinner',
    name: 'Spinner',
  },
  {
    class: 'moveDown',
    name: 'Move Down',
  },
  {
    class: 'moveUp',
    name: 'Move Up',
  },
  {
    class: 'moveLeftToRight',
    name: 'Move Right',
  },
  {
    class: 'moveRightToLeft',
    name: 'Move Left',
  },
  {
    class: 'slideIn',
    name: 'Slide In',
  },
];

export const bannerType = [
  {
    id: 1,
    name: 'Type 1',
  },
  {
    id: 2,
    name: 'Type 2',
  },
  {
    id: 3,
    name: 'Type 3',
  },
];

export const encryptedKeyForCredentials = {
  password: 'RpML0qe+o6atUKszlp7YmckLjX2uIR8sNyvxiTwlYOE=',
  email: 'IoJd4EcEZWigNoiSV1JzKp9dnw5ualsg1YpUxwZygu0=',
  mobile: 'SVuq5XhuNX491KJ9DTVyzHvQ8OyjCZEkmRTZn1Jf+pE=',
};

// Free / no-plan users' upload allowance. 0 = they get nothing (must start a
// trial / subscribe). Set to any positive number to give free users that many
// uploads again — all the gating below reads this dynamically.
export const ALLOWED_UPLOADS_COUNT = 0;

// Single source of truth for "can this free / no-plan user upload?".
// - ALLOWED_UPLOADS_COUNT <= 0 → free users are blocked outright (no API count
//   needed — we only care whether they're on a free / no plan).
// - ALLOWED_UPLOADS_COUNT  > 0 → they're blocked once their upload count (from the
//   API) reaches the cap, or they've hit their free storage limit.
// Paid / trial users are never blocked here (handled by their own limits).
export const isFreeUploadBlocked = (
  isFreePlan,
  uploadedCount = 0,
  hasReachedStorageLimit = false,
) => {
  if (!isFreePlan) return false;
  if (ALLOWED_UPLOADS_COUNT <= 0) return true;
  return uploadedCount >= ALLOWED_UPLOADS_COUNT || Boolean(hasReachedStorageLimit);
};
// Hard cap on how many photos may ride in a single upload flow. Past this the
// picker drops the extras and tells the user — an album can be topped up with
// further uploads, so nothing is permanently out of reach.
export const MAX_PHOTOS_PER_UPLOAD = 1000;
export const MAX_FREE_SIZE_GB = 15;

// --- Trial upload allowance -------------------------------------------------
// A trial account is capped by STORAGE (TRIAL_MAX_GB). The file-COUNT cap is a
// separate, OFF-by-default lever: flip TRIAL_FILE_CAP_ENABLED to true to ALSO
// cap by number of files (TRIAL_MAX_FILES, 1 photo = 1 file). Everything — the
// gate below and every bit of user-facing copy — derives from these constants,
// so switching the file cap on/off (or changing the GB) is a one-line change.
// The allowance is counted universally across every upload entry point as:
// content already on the server + anything currently uploading/queued + the
// files being added right now.
export const TRIAL_FILE_CAP_ENABLED = false; // ← set true to also cap by file count
export const TRIAL_MAX_FILES = 10; // only applies when TRIAL_FILE_CAP_ENABLED
export const TRIAL_MAX_GB = 10;
export const TRIAL_MAX_BYTES = TRIAL_MAX_GB * 1024 * 1024 * 1024;

// --- Server-driven limits (getConfig) --------------------------------------
// The real caps come from POST /getConfig (cached by useNewUI in localStorage
// 'ui.config'). For every allowance field the value means:
//   null    → UNLIMITED            (represented here as Infinity)
//   0       → NOT ALLOWED / blocked (0 allowance)
//   number  → a hard cap (bytes for *_size, file count for *_count)
// Fields: allowed_trial_upload_files_size / _count and
//         allowed_free_upload_files_size / _count.
// The TRIAL_MAX_* / MAX_FREE_* constants are only the fallback used before the
// config has loaded. Getters read the live config at call time so a backend
// change takes effect without a deploy.
const UI_CONFIG_KEY = 'ui.config';
const readServerConfig = () => {
  try {
    return JSON.parse(localStorage.getItem(UI_CONFIG_KEY)) || {};
  } catch (e) {
    return {};
  }
};

// Normalize one allowance field to a number: Infinity (unlimited), 0 (blocked),
// or the positive cap. `fallback` is used only when the config hasn't loaded.
const readLimit = (key, fallback) => {
  const c = readServerConfig();
  if (!Object.prototype.hasOwnProperty.call(c, key)) return fallback;
  const v = c[key];
  if (v === null) return Infinity; // unlimited
  if (typeof v === 'number' && v >= 0) return v; // 0 = blocked, >0 = cap
  return fallback;
};

// Trial storage cap (bytes) and file-count cap. Infinity = unlimited, 0 = blocked.
export const getTrialMaxBytes = () => readLimit('allowed_trial_upload_files_size', TRIAL_MAX_BYTES);
export const getTrialMaxFiles = () => readLimit(
  'allowed_trial_upload_files_count',
  TRIAL_FILE_CAP_ENABLED ? TRIAL_MAX_FILES : Infinity,
);

// Free-tier caps (config: 0/0 = free can't upload). Infinity = unlimited.
export const getFreeMaxBytes = () => readLimit('allowed_free_upload_files_size', MAX_FREE_SIZE_GB * 1024 * 1024 * 1024);
export const getFreeMaxFiles = () => readLimit('allowed_free_upload_files_count', Infinity);

export const getTrialMaxGB = () => {
  const b = getTrialMaxBytes();
  if (b === Infinity) return Infinity;
  const gb = b / (1024 * 1024 * 1024);
  return Number.isInteger(gb) ? gb : Math.round(gb * 100) / 100;
};

// A finite, non-zero file cap means the count limit is actively enforced.
export const isTrialFileCapEnabled = () => {
  const f = getTrialMaxFiles();
  return Number.isFinite(f);
};

// Human label for the trial allowance — derived from the live config so the
// wording (dashboard, billing, upgrade popup) never drifts from what's enforced.
export const getTrialAllowanceText = () => {
  const files = getTrialMaxFiles();
  const gb = getTrialMaxGB();
  const sizePart = gb === Infinity ? 'unlimited storage' : `${gb} GB`;
  if (Number.isFinite(files)) return `Up to ${files} files or ${sizePart}`;
  return gb === Infinity ? 'Unlimited uploads' : `Up to ${gb} GB`;
};

// Static fallback kept for backward-compat; prefer getTrialAllowanceText().
export const TRIAL_ALLOWANCE_TEXT = TRIAL_FILE_CAP_ENABLED
  ? `Up to ${TRIAL_MAX_FILES} files or ${TRIAL_MAX_GB} GB`
  : `Up to ${TRIAL_MAX_GB} GB`;

// Pure decision helper for the trial allowance. It only ever limits trial
// accounts — free users are stopped by isFreeUploadBlocked and paid users are
// unlimited, so both pass straight through here. Returns whether the batch is
// allowed, what's left, and (when blocked) which limit was hit.
export const trialUploadGate = ({
  isTrial = false,
  existingCount = 0,
  inflightCount = 0,
  usedBytes = 0,
  inflightBytes = 0,
  addCount = 0,
  addBytes = 0,
} = {}) => {
  if (!isTrial) {
    return {
      allowed: true, remainingFiles: Infinity, remainingBytes: Infinity, reason: null,
    };
  }
  const usedFiles = Math.max(0, existingCount) + Math.max(0, inflightCount);
  const usedSize = Math.max(0, usedBytes) + Math.max(0, inflightBytes);
  // Caps come from getConfig (Infinity = unlimited, 0 = blocked, number = cap).
  const maxFiles = getTrialMaxFiles();
  const maxBytes = getTrialMaxBytes();
  const remainingFiles = maxFiles === Infinity ? Infinity : Math.max(0, maxFiles - usedFiles);
  const remainingBytes = maxBytes === Infinity ? Infinity : Math.max(0, maxBytes - usedSize);
  const overFiles = maxFiles !== Infinity && Math.max(0, addCount) > remainingFiles;
  const overBytes = maxBytes !== Infinity && Math.max(0, addBytes) > remainingBytes;
  let reason = null;
  if (overFiles && overBytes) reason = 'both';
  else if (overFiles) reason = 'files';
  else if (overBytes) reason = 'size';
  return {
    allowed: !overFiles && !overBytes,
    remainingFiles,
    remainingBytes,
    reason,
  };
};
// TODO change key to env
export const GOOGLE_RECAPTCHA_KEY = '6Lc2vFgoAAAAAN7M6Qh2CrW2RJQYXlwOSCojWXRT';
export const CLOUDFLARE_TURNSTILE_SITE_KEY = '0x4AAAAAAB947200DAWaN-MF';

export const PLANS = {
  free: 'Free',
  best: 'Best',
};
